import { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";

import { fetcherFileUpload, fetcherSimple } from "utils/api";
import { isApiError, getApiErrorMessage } from "utils/api-error";
import { bumpPictureVersion } from "store/user";

// The server's picture whitelist (gobbyapi logo_accepted_formats):
// anything else 415s on every attempt, so it is rejected before arming.
const SERVER_IMAGE_TYPES = [
    "image/png",
    "image/jpeg",
    "image/bmp",
    "image/gif",
    "image/tiff",
    "image/raw",
];

// Pending profile-picture state for the Manage User dialog. Picture
// operations are deferred to the dialog's Save: picking a file or
// marking a delete only arms state here; runPendingOps() performs the
// upload/delete (and bumps the store's pictureVersion so every picture
// URL consumer refreshes); discard() reverts without a request.
//
// Failure policy (review finding I1): transient failures keep the op
// armed for an idempotent retry (the server replaces or removes the
// file). Non-retryable 4xx failures clear it instead -- the server will
// never accept them, and leaving them armed would block the dialog's
// whole Save, including unrelated text edits. A DELETE 404 means the
// picture is already gone, which is the desired end state: treated as
// success.
//
// Re-arming while an operation is in flight is allowed (the dialog
// disables the buttons, but the API guards anyway): runPendingOps
// captures the armed artifact's identity and only clears state the
// in-flight operation actually consumed, so a newer pick survives the
// stale continuation.
export default function useProfilePicture({ userId }) {
    const dispatch = useDispatch();

    const [pendingFile, setPendingFile] = useState(null);
    const [pendingDelete, setPendingDelete] = useState(false);
    const [previewSrc, setPreviewSrc] = useState(null);
    const [isRunning, setIsRunning] = useState(false);
    const runningRef = useRef(false);

    // Revoking keyed on the preview value: every transition (replace,
    // clear, unmount) revokes the outgoing URL, so no explicit revoke
    // calls are needed anywhere and nothing leaks if the hook unmounts
    // with a preview outstanding.
    useEffect(() => () => {
        if (previewSrc) {
            URL.revokeObjectURL(previewSrc);
        }
    }, [previewSrc]);

    const pickFile = (file) => {
        if (
            !file ||
            !file.type ||
            !SERVER_IMAGE_TYPES.includes(file.type)
        ) {
            toast.error(
                "Only Image Files are supported. Please ensure that the file you are loading is not excessively large and has the correct extension and name.",
            );
            return;
        }
        setPendingDelete(false);
        setPendingFile(file);
        setPreviewSrc(URL.createObjectURL(file));
    };

    const markDelete = () => {
        setPreviewSrc(null);
        setPendingFile(null);
        setPendingDelete(true);
    };

    // Returns true when this call ran an operation (or had none to
    // run), false when it was a re-entry while already running: the
    // dialog's submit wrapper uses that to skip a duplicate text-field
    // PUT on double-click. The running flags reset in a finally so a
    // failed DELETE cannot wedge the dialog's Save (review finding I1).
    const runPendingOps = async () => {
        if (runningRef.current) {
            return false;
        }
        runningRef.current = true;
        setIsRunning(true);
        try {
            if (pendingDelete) {
                try {
                    await fetcherSimple(`/v2/user/${userId}/picture`, "DELETE");
                } catch (err) {
                    if (isApiError(err) && err.status === 404) {
                        // Already gone: the desired end state.
                    } else {
                        toast.error(
                            `Error deleting picture: ${err?.body?.message || getApiErrorMessage(err?.status, "picture")}`,
                        );
                        if (isApiError(err) && !err.isRetryable) {
                            // The server will never let this delete
                            // succeed: clear it so the dialog's Save can
                            // still run the text-field PUT.
                            setPendingDelete(false);
                        }
                        throw err;
                    }
                }
                setPendingDelete(false);
                dispatch(bumpPictureVersion());
                return true;
            }
            if (pendingFile) {
                const file = pendingFile;
                const preview = previewSrc;
                try {
                    await fetcherFileUpload(
                        `/v2/user/${userId}/picture`,
                        "POST",
                        "picture",
                        file,
                    );
                } catch (err) {
                    toast.error(
                        `Error uploading Image${err?.body?.message ? `: ${err.body.message}` : ", unknown issue in uploading image. Please try a different size or type."}`,
                    );
                    if (isApiError(err) && !err.isRetryable) {
                        // The server will never accept this file (e.g. a
                        // rejected content type): clear it so the dialog's
                        // Save can proceed with the text fields.
                        setPendingFile((current) =>
                            current === file ? null : current,
                        );
                        setPreviewSrc((current) =>
                            current === preview ? null : current,
                        );
                    }
                    throw err;
                }
                // Only clear what this operation consumed: a file picked
                // while the upload was in flight must survive.
                setPendingFile((current) => (current === file ? null : current));
                setPreviewSrc((current) =>
                    current === preview ? null : current,
                );
                dispatch(bumpPictureVersion());
            }
            return true;
        } finally {
            runningRef.current = false;
            setIsRunning(false);
        }
    };

    const discard = () => {
        setPreviewSrc(null);
        setPendingFile(null);
        setPendingDelete(false);
    };

    return {
        previewSrc,
        pendingDelete,
        hasPendingOps: Boolean(pendingFile) || pendingDelete,
        isRunning,
        pickFile,
        markDelete,
        runPendingOps,
        discard,
    };
}
